import { type MutableRefObject, useEffect } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { loadImageNaturalSize } from '@/lib/photos/photo-fill-resolution'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * Re-measures the natural pixel size of checkpoint-loaded photo fills and backs out their real
 * zoom (in memory only).
 */
export function usePhotoFillNaturalSize({
  internalShapeManagerRef,
  checkpointShapes,
  checkpointLoadKey,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  checkpointShapes: CenterCanvasConfig['checkpointShapes']
  checkpointLoadKey: CenterCanvasConfig['checkpointLoadKey']
}) {
  // Photo fills loaded from a checkpoint carry a placeholder `scale` (natural pixel size
  // isn't part of the wire contract — see checkpoint-to-shapes.ts `readShapeFillImage`).
  // Re-measure each fill's photo the same way placement does (`loadImageNaturalSize`) and
  // back out the real zoom once known. In-memory only — never persisted just from loading a
  // page, matching the legacy app (it never resaves anything purely from rendering). Naturally
  // idempotent: shapes already corrected are filtered out by the `naturalWidth == null` check.
  useEffect(() => {
    const pendingFills = (checkpointShapes ?? []).filter(
      (shape): shape is typeof shape & { photoFill: NonNullable<typeof shape.photoFill> } =>
        shape.photoFill != null && shape.photoFill.naturalWidth == null
    )

    if (pendingFills.length === 0) return

    let cancelled = false

    void Promise.all(
      pendingFills.map(async (shape) => {
        const fill = shape.photoFill
        const naturalSize = await loadImageNaturalSize(fill.previewUrl)

        if (cancelled || !naturalSize || naturalSize.width <= 0 || naturalSize.height <= 0) {
          return
        }

        const coverScale = Math.max(
          shape.width / naturalSize.width,
          shape.height / naturalSize.height
        )
        const scale =
          fill.persistedRenderedWidth != null && coverScale > 0
            ? fill.persistedRenderedWidth / naturalSize.width / coverScale
            : fill.scale

        internalShapeManagerRef.current?.updateShape(shape.id, {
          photoFill: {
            ...fill,
            naturalWidth: naturalSize.width,
            naturalHeight: naturalSize.height,
            scale,
          },
        })
      })
    )

    return () => {
      cancelled = true
    }
  }, [checkpointShapes, checkpointLoadKey, internalShapeManagerRef])
}
